import { CheckCircle, Info, Sparkle, Warning, XCircle } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';

export type WellTone = 'default' | 'brand' | 'info' | 'success' | 'warning' | 'danger';

/**
 * The Figma Well (page ↳ Well & Card, 52:90): 16px padding, radius/lg, a 20px
 * icon 12px from a Content column: a Text stack (gap space/1) of Title in
 * Label/Medium text/primary and Body in Body/Small text/secondary, then the
 * optional Action space/2 below it. Fill, 1px inside stroke and icon colour
 * per Tone; Brand's stroke is brand/subtle (its fill), so every tone is the
 * same height.
 */
const well = tv({
  slots: {
    root: 'flex w-full items-start gap-3 rounded-lg p-4',
    icon: 'size-5 shrink-0',
    content: 'flex min-w-0 flex-1 flex-col gap-2',
    text: 'flex flex-col gap-1',
    title: 'text-label-md text-text-primary',
    body: 'text-body-sm text-text-secondary',
    action: 'flex',
  },
  variants: {
    tone: {
      default: {
        root: 'bg-bg-subtle border-border-subtle border',
        icon: 'text-icon-secondary',
      },
      brand: {
        root: 'bg-brand-subtle border-brand-subtle border',
        icon: 'text-icon-brand',
      },
      info: {
        root: 'bg-status-info-bg border-status-info-border border',
        icon: 'text-status-info-fg',
      },
      success: {
        root: 'bg-status-success-bg border-status-success-border border',
        icon: 'text-status-success-fg',
      },
      warning: {
        root: 'bg-status-warning-bg border-status-warning-border border',
        icon: 'text-status-warning-fg',
      },
      danger: {
        root: 'bg-status-danger-bg border-status-danger-border border',
        icon: 'text-status-danger-fg',
      },
    },
  },
});

/** Figma "Icon swap" default per Tone (Phosphor Regular). */
const TONE_ICON: Record<WellTone, ElementType<{ className?: string }>> = {
  default: Info,
  brand: Sparkle,
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: XCircle,
};

export type WellProps = Omit<ComponentProps<'div'>, 'title'> & {
  /** Figma "Tone": Default for tips and grouped settings; Brand for announcements; status tones for inline feedback. */
  tone?: WellTone;
  /** Figma "Title" (Show title). */
  title?: ReactNode;
  /** Figma "Icon swap". Defaults to the tone's icon; `false` hides it (Show icon off). */
  icon?: ElementType<{ className?: string }> | false;
  /** Figma "Action": a Small Outline Button (or a Link) under the body. */
  action?: ReactNode;
  /** Figma "Body". */
  children?: ReactNode;
};

/**
 * Inset container for hints, inline status and grouped content (Figma:
 * Components ↳ Well & Card ↳ Well). Not dismissible - use a Toast for
 * transient messages. Static by default; give it `role="status"` when it
 * appears in response to an action.
 */
export function Well({
  tone = 'default',
  title,
  icon,
  action,
  className,
  children,
  ...props
}: WellProps) {
  const styles = well({ tone });
  const Icon = icon === false ? null : (icon ?? TONE_ICON[tone]);
  return (
    <div
      data-slot="well"
      data-tone={tone}
      className={styles.root({ className })}
      {...props}
    >
      {Icon && <Icon aria-hidden data-slot="well-icon" className={styles.icon()} />}
      <div data-slot="well-content" className={styles.content()}>
        {(title || children) && (
          <div data-slot="well-text" className={styles.text()}>
            {title && (
              <div data-slot="well-title" className={styles.title()}>
                {title}
              </div>
            )}
            {children && (
              <div data-slot="well-body" className={styles.body()}>
                {children}
              </div>
            )}
          </div>
        )}
        {action && (
          <div data-slot="well-action" className={styles.action()}>
            {action}
          </div>
        )}
      </div>
    </div>
  );
}
